import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import {
  acceptJargonSuggestion,
  dismissJargonSuggestion,
  fetchJargonSuggestions,
  type IJargonSuggestion,
} from "../../../../lib/api"
import { notifyError } from "../../../../lib/notify"
import { queryKeys } from "../../../../lib/query-keys"
import { type IMeetingJargonGate, shouldLoadMeetingJargon } from "./meeting-jargon-gate"

export interface IUseMeetingJargon {
  jargonSuggestions: IJargonSuggestion[]
  jargonBusyId: string | null
  onAcceptJargon: (sid: string, term?: string) => void
  onDismissJargon: (sid: string) => void
}

export type { IMeetingJargonGate }

/**
 * Per-meeting jargon-suggestion query + accept/dismiss mutations, extracted
 * verbatim from the meeting-detail route. Self-contained: it owns the pending
 * suggestions query (a loaded, finished Full meeting only) and the
 * accept/dismiss flow with its busy-id tracking and cache invalidation. Keeps
 * the route's pipeline / SSE / render-gate logic untouched.
 */
export function useMeetingJargon(
  company: CompanyApi | null,
  orgId: string | null,
  meetingId: string,
  isLive: boolean,
  gate: IMeetingJargonGate,
): IUseMeetingJargon {
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  // Per-word jargon suggestions for the insights rail. Only once the meeting
  // has loaded, is no longer live, and is Full. Suggestions are produced
  // post-processing, and Lite hides Jargon.
  const { data: jargonSuggestions = [] } = useQuery({
    queryKey: queryKeys.jargonSuggestions(orgId, meetingId),
    queryFn: () => (company ? fetchJargonSuggestions(company, { status: "pending", meetingId }) : Promise.resolve([])),
    enabled: shouldLoadMeetingJargon(company != null, isLive, gate),
  })

  const [jargonBusyId, setJargonBusyId] = useState<string | null>(null)
  const jargonMutation = useMutation({
    mutationFn: ({ action, sid, term }: { action: "accept" | "dismiss"; sid: string; term?: string }) => {
      if (!company) throw new Error("company unavailable")
      return action === "accept" ? acceptJargonSuggestion(company, sid, term) : dismissJargonSuggestion(company, sid)
    },
    onMutate: ({ sid }) => setJargonBusyId(sid),
    onSuccess: (res, { action }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jargonSuggestions(orgId, meetingId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) })
      notifications.show({
        message:
          action === "accept"
            ? i18n._(msg`Added "${res.term}" to your dictionary`)
            : i18n._(msg`Dismissed "${res.term}"`),
        color: action === "accept" ? "success" : "gray",
      })
    },
    onError: (err: Error) => notifyError(err),
    onSettled: () => setJargonBusyId(null),
  })

  const onAcceptJargon = useCallback(
    (sid: string, term?: string) => jargonMutation.mutate({ action: "accept", sid, term }),
    [jargonMutation],
  )
  const onDismissJargon = useCallback(
    (sid: string) => jargonMutation.mutate({ action: "dismiss", sid }),
    [jargonMutation],
  )

  return { jargonSuggestions, jargonBusyId, onAcceptJargon, onDismissJargon }
}
